import type { TFunction } from "i18next";
import moment from "moment";
import { RefObject } from "react";
import React from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import type { DefaultTheme } from "styled-components";
import { LoadingIcon } from "../components/Icons/Icons";
import { StatusBadge, type StatusColor } from "../components/Status/Status";
import { ToggleSwitchV2 } from "../components/ToggleSwitch/ToggleSwitch";
import type { User, UUID, Permission } from "../types/types";
import type {
  AttributeSchema,
  AttributeValue,
  DocumentAttributeValue,
  ObjectReferenceResult,
  ProductReference,
} from "../types/types.PIM";
import { DocumentAttributePreview } from "../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentAttributePreview";
import {
  getUserName,
  getUserRefTooltip,
  getCollectionUserMultiRefTooltip,
  toTitleCase,
} from "./util";
import { LinkAttributeValueSchema } from "./zod.util";
import { getAddressDisplayLines } from "../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/addressDisplay.utils";
import {
  TextMdRegular,
  TextSmRegular,
} from "../components/Typography/TypographyV2";
import { AddressValue } from "../pages/admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { ObjectReferencePreviewTooltip } from "../components/ObjectReference/ObjectReferencePreviewTooltip";
import {
  buildObjectRefPreviewDisplayRows,
  getReferenceRestrictedMessage,
  getObjectReferenceDisplayHref,
  isObjectReferenceDisplayAccessible,
  isProductReferenceValueDisplayAccessible,
} from "../components/ObjectReference/utils";
import type { ReferenceDisplayAccessContext } from "../components/ObjectReference/utils";
import {
  CollectionReferenceCell,
  MultiObjectReferenceTooltip,
} from "../components/CollectionReferenceCell";
import { objectRefToProductReference } from "../components/ObjectReference/ObjectReferenceAttributeDisplay";
import { createPortal } from "react-dom";
import { spacingTokens } from "../designTokens";

/** Carries document display data for collection table cells. */
export type DocumentCollectionValue = DocumentAttributeValue & {
  url?: string;
};

export type CollectionAttributeDisplayValue =
  | string
  | boolean
  | number
  | {
      name: string;
      status?: AttributeValue["status"];
      badge_color?: StatusColor;
    }[]
  | ProductReference
  | ObjectReferenceResult
  | User[]
  | { display_text: string; url: string }
  | DocumentCollectionValue[]
  | null;

export type GetCollectionAttributeDisplayValueOptions = {
  isPortfolio?: boolean;
  isLoading?: boolean;
  theme: DefaultTheme;
  t: TFunction;
  user: User | null | undefined;
  roleIsSellerAdmin?: boolean;
  hasPermission?: (permission: Permission) => boolean;
  storePath: string;
  adminPath: string;
  collectionTableRef?: RefObject<HTMLDivElement>;
  onDocumentMoreClick?: (
    docs: DocumentCollectionValue[],
    attributeName: string
  ) => void;
  onObjectReferenceMoreClick?: (
    references: ObjectReferenceResult[],
    attributeName: string,
    targetObjectType?: string
  ) => void;
  onProductReferenceMoreClick?: (
    references: ProductReference[],
    attributeName: string
  ) => void;
};

export const multiSelectInput = (
  value:
    | string
    | boolean
    | number
    | { name: string }[]
    | ProductReference
    | { display_text: string; url: string }
    | User[]
    | null,
  isUser: boolean,
  collectionTableRef?: RefObject<HTMLDivElement>
): React.ReactNode => {
  const multiselectValue = isUser
    ? (value as User[])
    : (value as { name: string }[] | null | undefined);
  const cellID = Math.floor((1 + Math.random()) * 0x10000);
  return multiselectValue?.length ? (
    <>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          display: "inline-block",
        }}
        data-for={`row-tooltip-${cellID}`}
        data-tip={""}
      >
        {isUser
          ? (multiselectValue as User[])
              .map((u) => getUserName(u.firstname, u.lastname))
              .join(", ")
          : (multiselectValue as { name: string }[])
              .map(({ name }) => name)
              .join(", ")}{" "}
      </span>
      {(multiselectValue?.length > 1 || isUser) &&
        createPortal(
          <ReactTooltip
            id={`row-tooltip-${cellID}`}
            place="top"
            data-html={true}
            effect="solid"
            multiline={true}
            overridePosition={({ left, top }, _e, _t, node) => {
              const c = collectionTableRef?.current,
                r = c?.getBoundingClientRect(),
                w = (node as HTMLElement)?.clientWidth || 0;
              return r
                ? {
                    left: Math.max(
                      window.scrollX + r.left + 8,
                      Math.min(left, window.scrollX + r.right - w - 8)
                    ),
                    top,
                  }
                : { left, top };
            }}
          >
            <div>
              <ul
                className="multiselect_tooltip_list"
                style={{
                  listStyle: "inside",
                  margin: 0,
                  padding: `${spacingTokens.spacing_sm} 0`,
                }}
              >
                {isUser
                  ? (multiselectValue as User[]).map((u) => (
                      <li className="multiselect_tooltip_list_item" key={u.id}>
                        {getCollectionUserMultiRefTooltip(u)}
                      </li>
                    ))
                  : (multiselectValue as { name: string }[]).map(({ name }) => (
                      <li className="multiselect_tooltip_list_item" key={name}>
                        {name}
                      </li>
                    ))}
              </ul>
            </div>
          </ReactTooltip>,
          document.body
        )}
    </>
  ) : (
    "--"
  );
};

export function getCollectionAttributeDisplayValue(
  col: AttributeSchema,
  value: CollectionAttributeDisplayValue,
  options: GetCollectionAttributeDisplayValueOptions
): React.ReactNode {
  const {
    isPortfolio = false,
    isLoading = false,
    theme,
    t,
    user,
    roleIsSellerAdmin = false,
    hasPermission,
    storePath,
    adminPath,
    collectionTableRef,
    onDocumentMoreClick,
    onObjectReferenceMoreClick,
    onProductReferenceMoreClick,
  } = options;

  if (isLoading) {
    return (
      <LoadingIcon
        width={16}
        height={16}
        fill={theme.resolve("color_icon_primary")}
      />
    );
  }
  if (col.is_not_applicable) {
    return "--";
  }
  switch (col.input_type) {
    case "single_select": {
      const singleSelectValue = (
        value as {
          name: string;
          status?: AttributeValue["status"];
          badge_color?: StatusColor;
        }[]
      )?.[0];
      if (singleSelectValue?.status) {
        const { color, status } = singleSelectValue.status;
        return <StatusBadge color={color} text={toTitleCase(status)} />;
      }
      if (singleSelectValue?.badge_color) {
        return (
          <StatusBadge
            color={singleSelectValue.badge_color}
            text={singleSelectValue.name}
          />
        );
      }
      return singleSelectValue?.name ?? "--";
    }
    case "product_reference": {
      const references = (
        Array.isArray(value) ? value : value ? [value] : []
      ) as ProductReference[];
      if (!references.length) return "--";

      const accessContext: ReferenceDisplayAccessContext = {
        isPortfolio: Boolean(isPortfolio),
        user,
        roleIsSellerAdmin: Boolean(roleIsSellerAdmin),
        hasPermission,
      };
      const isProductRefAccessible = (reference: ProductReference) =>
        isProductReferenceValueDisplayAccessible(reference, accessContext);
      const getProductRefRestrictedTooltip = (reference: ProductReference) =>
        getReferenceRestrictedMessage(reference, t);

      return (
        <CollectionReferenceCell
          references={references}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
          isAccessible={isProductRefAccessible}
          getRestrictedTooltip={getProductRefRestrictedTooltip}
          t={t}
          onMoreClick={
            onProductReferenceMoreClick
              ? () => onProductReferenceMoreClick(references, col.name)
              : undefined
          }
        />
      );
    }
    case "user":
      if (col.is_multiple_choice) {
        return multiSelectInput(value as User[], true, collectionTableRef);
      } else {
        const userRef = (value as User[])?.[0];
        return (
          <>
            <span
              data-tip={userRef ? getUserRefTooltip(userRef) : ""}
              data-for={userRef?.id}
              data-html={true}
            >
              {getUserName(userRef?.firstname, userRef?.lastname)}
            </span>
            <ReactTooltip
              delayHide={200}
              id={userRef?.id}
              effect="solid"
              data-html={true}
            />
          </>
        );
      }
    case "object_reference": {
      const objectReferences = (
        Array.isArray(value) ? value : value ? [value] : []
      ) as ObjectReferenceResult[];
      if (!objectReferences.length) return "--";

      const linkOptions = { isPortfolio, storePath, adminPath };
      const accessContext: ReferenceDisplayAccessContext = {
        isPortfolio,
        user,
        roleIsSellerAdmin,
        hasPermission,
        targetObjectType: col.target_object_type,
      };

      const productRefs = objectReferences.map((ref) =>
        objectRefToProductReference(ref)
      );

      const objectRefById = new Map(
        objectReferences.map((ref) => [ref.id, ref])
      );

      const isRefAccessible = (ref: ProductReference) => {
        const originalRef = objectRefById.get(ref.id);
        if (!originalRef) return false;
        return isObjectReferenceDisplayAccessible(originalRef, accessContext);
      };

      const getRefRestrictedTooltip = (ref: ProductReference) => {
        const originalRef = objectRefById.get(ref.id);
        if (!originalRef) return t("Product informations is not accessible");
        return getReferenceRestrictedMessage(originalRef, t);
      };

      const renderObjectRefWithTooltip = (
        ref: ProductReference,
        key: string
      ) => {
        const originalRef = objectRefById.get(ref.id);
        if (!originalRef) return <span key={key}>{ref.name}</span>;

        const previewRows = buildObjectRefPreviewDisplayRows(originalRef, t);
        const previewAttrs = previewRows.map((r) => ({
          label: r.label,
          value: r.value,
        }));
        const href = getObjectReferenceDisplayHref(originalRef, linkOptions);

        return (
          <ObjectReferencePreviewTooltip
            key={key}
            tooltipId={`object-ref-${originalRef.id}-${key}`}
            previewAttributes={previewAttrs}
          >
            {href ? (
              <Link to={href}>{originalRef.name}</Link>
            ) : (
              <span>{originalRef.name}</span>
            )}
          </ObjectReferencePreviewTooltip>
        );
      };

      const isOriginalRefAccessible = (ref: ObjectReferenceResult) =>
        isObjectReferenceDisplayAccessible(ref, accessContext);

      const getOriginalRefHref = (ref: ObjectReferenceResult) =>
        getObjectReferenceDisplayHref(ref, linkOptions);

      const getOriginalRefRestrictedTooltip = (ref: ObjectReferenceResult) =>
        getReferenceRestrictedMessage(ref, t);

      const renderMultiObjectRef = () => (
        <MultiObjectReferenceTooltip
          references={objectReferences}
          isAccessible={isOriginalRefAccessible}
          getHref={getOriginalRefHref}
          getRestrictedTooltip={getOriginalRefRestrictedTooltip}
          t={t}
          onMoreClick={
            onObjectReferenceMoreClick
              ? () =>
                  onObjectReferenceMoreClick(
                    objectReferences,
                    col.name,
                    col.target_object_type
                  )
              : undefined
          }
        />
      );

      return (
        <CollectionReferenceCell
          references={productRefs}
          isPortfolio={isPortfolio}
          storePath={storePath}
          adminPath={adminPath}
          isAccessible={isRefAccessible}
          getRestrictedTooltip={getRefRestrictedTooltip}
          renderAccessibleRef={renderObjectRefWithTooltip}
          renderMultiReference={renderMultiObjectRef}
          t={t}
        />
      );
    }
    case "numeric":
      return value ? value : "--";
    case "date":
      return value ? moment(value as string).format("MMM DD, YYYY") : "--";
    case "form_field":
      return value ? value : "--";
    case "multiline_entry":
      return (
        <>
          {value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: `<div class="ql-editor">${value as string}</div>`,
              }}
            />
          ) : (
            "--"
          )}
        </>
      );
    case "toggle":
    case "checkbox":
      return isPortfolio ? (
        <span>{!!value ? "Yes" : "No"}</span>
      ) : (
        <>
          <ToggleSwitchV2
            label={""}
            name={col.id}
            checked={(value as unknown as boolean) ?? false}
            disabled={true}
          />
        </>
      );
    case "multi_select":
      return multiSelectInput(
        value as { name: string }[] | null,
        false,
        collectionTableRef
      );
    case "link": {
      const parsed = LinkAttributeValueSchema.safeParse(value);
      if (parsed.success && parsed.data.url) {
        return (
          <a
            href={parsed.data.url as string}
            target={"_blank"}
            rel="noreferrer"
          >
            {parsed.data.display_text}
          </a>
        );
      } else if (
        parsed.success &&
        !parsed.data.url &&
        parsed.data.display_text !== ""
      ) {
        return parsed.data.display_text;
      } else return "--";
    }
    case "address": {
      const address = value as AddressValue | null;
      if (!address) return "--";
      const { postalLines, metaFields } = getAddressDisplayLines(address, t);
      if (!postalLines.length && !metaFields.length) return "--";
      return (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <TextMdRegular
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {postalLines.join(", ")}
            </TextMdRegular>
            {metaFields.length > 0 && (
              <TextSmRegular
                style={{
                  opacity: 0.6,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {metaFields
                  .map(({ label, value }) => `${label}: ${value}`)
                  .join(", ")}
              </TextSmRegular>
            )}
          </div>
        </>
      );
    }
    case "document": {
      const docs = value as DocumentCollectionValue[];
      if (!docs?.length) return "--";
      return (
        <DocumentAttributePreview
          attribute={{
            ...col,
            values: docs.map((docVal, idx) => ({
              id: docVal.asset_id as UUID,
              priority: idx,
              enum_variant: null,
              attribute_id: col.id as UUID,
              value: docVal as DocumentAttributeValue,
              data_type: "document" as const,
              cdn_url: docVal.url,
              asset_name: docVal.display_name,
              asset_type: docVal.asset_type,
              content_type: docVal.content_type,
              host: docVal.host_name
                ? { name: docVal.host_name, id: "" }
                : undefined,
            })),
          }}
          maxVisible={1}
          onMoreClick={(vals) =>
            onDocumentMoreClick?.(
              (vals ?? []).map((av) => ({
                ...(av.value as DocumentAttributeValue),
                url: av.cdn_url ?? av.signed_url ?? av.preview_url,
              })),
              col.name
            )
          }
        />
      );
    }
    default:
      return "--";
  }
}
